import { useAtom } from "@dbeining/react-atom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, Outlet, useMatch, useNavigate, useParams } from "react-router-dom";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import type { ILocationDetail } from "../../customTypings/location/Location";
import { locationsService } from "../../services/locations/locations.service";
import {
    buildRecordLifecycleConfirmProps,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    getUserFacingErrorMessage,
    ListPageTitles,
    loadingPageTitle,
    locationAssociationsPageTitle,
    logErrorForDev,
    SectionLabels,
    showToastError,
    showToastSuccess,
} from "../../utils";
import {
    canMutateLocations,
    getLocationAdminActionSuccessMessage,
    type LocationAdminConfirmKind,
} from "../LocationsListPage/locationAdminListActions";
import {
    buildLocationEditHeaderActions,
    type LocationEditActiveView,
} from "./locationEditHeaderActions";
import type { LocationEditOutletContext } from "./locationEditOutletContext";
import { formatLocationMetaByLine, formatLocationMetaDate } from "./locationEditMetaFormatters";

type ConfirmKind = LocationAdminConfirmKind | null;

const LocationEditLayout = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateLocations(roleNames);

    const isAssociationsRoute = Boolean(useMatch("/locations/:id/associations"));
    const activeView: LocationEditActiveView = isAssociationsRoute ? "associations" : "edit";

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [locationDetail, setLocationDetail] = useState<ILocationDetail | null>(null);
    const [locationName, setLocationName] = useState("");
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);

    const applyLocationDetail = useCallback((location: ILocationDetail) => {
        setLocationDetail(location);
        setLocationName(location.name);
        setMetaEnabled(location.isEnabled);
        setMetaDeleted(location.isDeleted);
        setCreatedAt(location.createdAtUtc);
        setUpdatedAt(location.updatedAtUtc);
        setCreatedByDisplayName(location.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(location.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(location.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(location.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(location.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(location.disabledByDisplayName ?? null);
    }, []);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        locationsService
            .getById(id)
            .then(applyLocationDetail)
            .catch((e: unknown) => {
                logErrorForDev("LocationEditLayout.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load location."));
            })
            .finally(() => setLoading(false));
    }, [id, applyLocationDetail]);

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const headerMenuActions = useMemo(() => {
        if (!id) return [];
        return buildLocationEditHeaderActions(navigate, id, setConfirmKind, activeView);
    }, [navigate, id, activeView]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.location,
                locationName || `this ${EntityLabels.location.toLowerCase()}`
            ),
        [confirmKind, locationName]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || !kind) return;
        const op =
            kind === "disable"
                ? locationsService.disable(id)
                : kind === "enable"
                  ? locationsService.enable(id)
                  : kind === "softDelete"
                    ? locationsService.softDelete(id)
                    : locationsService.restore(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getLocationAdminActionSuccessMessage(kind, locationName));
            if (kind === "softDelete") {
                navigate("/locations", { replace: true });
                return;
            }
            return locationsService.getById(id).then(applyLocationDetail);
        }).catch((e: unknown) => {
            setConfirmKind(null);
            showToastError(getUserFacingErrorMessage(e, "Could not delete location."));
        });
    };

    const outletContext = useMemo((): LocationEditOutletContext | undefined => {
        if (!id) return undefined;
        return {
            locationId: id,
            locationName,
            locationDetail,
            metaDeleted,
            metaEnabled,
            setConfirmKind,
        };
    }, [id, locationName, locationDetail, metaDeleted, metaEnabled]);

    const pageTitle =
        activeView === "associations"
            ? locationAssociationsPageTitle(EntityLabels.location)
            : editPageTitle(EntityLabels.location, false);

    const headerNavLabel = activeView === "edit" ? "View Associations" : "Edit Location";

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage locations.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.location)} />
            </Container>
        );
    }

    if (loadError || !id) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError ?? "Location not found."}</Alert>
                <Link to="/locations">Back to locations</Link>
            </Container>
        );
    }

    const breadcrumbItems = [
        { text: SectionLabels.operations, to: "/operations" },
        { text: ListPageTitles.locations, to: "/locations" },
        { text: editFormBreadcrumbLabel(false), to: `/locations/${id}` },
        ...(activeView === "associations" ? [{ text: "Associations" as const }] : []),
    ];

    return (
        <Container fluid>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <ResponsiveBreadcrumb className="mb-3" items={breadcrumbItems} />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {pageTitle}
                </h1>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {!isNarrowScreen && (
                        <Button
                            variant="secondary"
                            onClick={() =>
                                activeView === "edit"
                                    ? navigate(`/locations/${id}/associations`)
                                    : navigate(`/locations/${id}`)
                            }
                        >
                            {headerNavLabel}
                        </Button>
                    )}
                    {isNarrowScreen && (
                        <EllipsisActionMenu
                            id="location-edit-actions-dropdown"
                            ariaLabel="Location actions"
                            actions={headerMenuActions}
                            item={headerMenuItem}
                        />
                    )}
                </div>
            </div>

            <RecordAuditMetaBar
                formatDate={formatLocationMetaDate}
                formatByLine={formatLocationMetaByLine}
                createdAt={createdAt}
                createdByDisplayName={createdByDisplayName}
                updatedAt={updatedAt}
                lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                isDeleted={metaDeleted}
                isEnabled={metaEnabled}
                deletedAuditAtUtc={deletedAuditAtUtc}
                deletedByDisplayName={deletedByDisplayName}
                disabledAuditAtUtc={disabledAuditAtUtc}
                disabledByDisplayName={disabledByDisplayName}
            />

            {outletContext && <Outlet context={outletContext} />}

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { LocationEditLayout };
